import React, { useState } from 'react';
import { Home, AlertTriangle, BarChart3, Cpu, Bell, X, Sun, Moon, Monitor, Palette, Settings, FlaskConical } from "lucide-react";
import { PulseLogo } from './pulse-logo';
import { useTheme } from '../../contexts/ThemeContext';
import { APP_VERSION } from '../../version';

interface CustomOverlaySidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CustomOverlaySidebar: React.FC<CustomOverlaySidebarProps> = ({ isOpen, onClose }) => {
  const currentPath = window.location.pathname;
  const { theme, setTheme } = useTheme();
  const [appearanceExpanded, setAppearanceExpanded] = useState(false);

  const version = APP_VERSION;

  const navigationItems = [
    { title: "Dashboard", url: "/dashboard", icon: Home },
    { title: "Trial Machines", url: "/trial-machines", icon: FlaskConical },
    { title: "Gateways", url: "/gateways", icon: Cpu },
    { title: "Gateway Alerts", url: "/gateway-alerts", icon: Bell },
    { title: "Critical Alerts", url: "/critical-alerts", icon: AlertTriangle },
    { title: "Custom Alert Rules", url: "/custom-alert-rules", icon: Settings },
    { title: "Analytics", url: "/analytics", icon: BarChart3 },
  ];

  const themeOptions = [
    { value: 'light' as const, label: 'Light', icon: Sun },
    { value: 'dark' as const, label: 'Dark', icon: Moon },
    { value: 'system' as const, label: 'System', icon: Monitor },
  ];

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRight: '1px solid var(--border-color)'
        }}
      >
        {/* Header */}
        <div className="p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center justify-between">
            <PulseLogo size="md" showText={true} />
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors hover:bg-[var(--bg-tertiary)]"
              style={{
                color: 'var(--text-secondary)'
              }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 p-4">
          <nav className="space-y-2">
            {navigationItems.map((item) => {
              const isActive = currentPath === item.url || (item.url === '/dashboard' && (currentPath === '/' || currentPath === '/dashboard'));
              return (
                <div key={item.title} className="relative">
                  <a
                    href={item.url}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 relative ${
                      isActive
                        ? 'text-white font-medium'
                        : 'hover:bg-[var(--bg-tertiary)]'
                    }`}
                    style={!isActive ? { color: 'var(--text-primary)' } : {}}
                  >
                    {/* Full-width selection background */}
                    {isActive && (
                      <div
                        className="absolute inset-0 rounded-lg"
                        style={{
                          background: 'linear-gradient(135deg, #00A9A7 0%, #008a87 50%, #006b69 100%)',
                          boxShadow: '0 4px 12px rgba(0, 169, 167, 0.3)'
                        }}
                      />
                    )}
                    <item.icon className="h-5 w-5 relative z-10" />
                    <span className="text-sm relative z-10">{item.title}</span>
                  </a>
                </div>
              );
            })}

            {/* Theme Selector */}
            <div className="mt-6">
              <button
                onClick={() => setAppearanceExpanded(!appearanceExpanded)}
                className="flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors hover:bg-[var(--bg-tertiary)]"
                style={{ color: 'var(--text-secondary)' }}
              >
                <div className="flex items-center gap-3">
                  <Palette className="h-5 w-5" />
                  <span className="text-sm">APPEARANCE</span>
                </div>
                <svg
                  className={`h-4 w-4 transition-transform duration-200 ${appearanceExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Collapsible Theme Section */}
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                appearanceExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                {/* Theme Options */}
                <div className="space-y-1 mt-2">
                  {themeOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = theme === option.value;
                    return (
                      <div key={option.value} className="relative">
                        <button
                          onClick={() => setTheme(option.value)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 text-left relative ${
                            isSelected
                              ? 'text-white font-medium'
                              : 'hover:bg-[var(--bg-tertiary)]'
                          }`}
                          style={!isSelected ? { color: 'var(--text-primary)' } : {}}
                        >
                          {/* Full-width selection background */}
                          {isSelected && (
                            <div
                              className="absolute inset-0 rounded-xl"
                              style={{
                                background: 'linear-gradient(135deg, #ff8697 0%, #ff6b7d 50%, #ff4757 100%)',
                                boxShadow: '0 4px 12px rgba(255, 134, 151, 0.3)'
                              }}
                            />
                          )}
                          <Icon className="h-5 w-5 relative z-10" />
                          <span className="text-sm relative z-10">{option.label}</span>
                          {isSelected && (
                            <div className="ml-auto w-2 h-2 rounded-full bg-white shadow-sm relative z-10" />
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </nav>
        </div>

        {/* Footer */}
        <div className="p-4" style={{ borderTop: '1px solid var(--border-color)' }}>
          <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {version} • Online
          </div>
        </div>
      </div>
    </>
  );
};
